/**
 * L.3: URL → `useWorkspaceFileStore` sync.
 *
 * Mounts once at the app shell. Watches `location.pathname`:
 *   - `/clients/:c/matters/:m/cases/:id/files/:path*` → `openFile({path})`
 *   - any other path → `closeFile()`
 *
 * Consequences this sync delivers for free:
 *   - Cold-load of a file URL opens the pane directly (layout-plan
 *     §4.4 bidirectional sync).
 *   - Navigating to a different case (or leaving the hierarchy)
 *     auto-resets the pane (layout-plan §3.5 "Closes when: user
 *     navigates to a different case").
 *
 * Store → URL direction is owned by callers (tree-click handlers
 * and close-button overrides pass a `navigate()` call alongside
 * store mutations, mirroring the L.1 convention for
 * `useActiveContextStore`).
 *
 * Dev-only probe routes (`/__probe/*`) are skipped — they own their
 * own file-store state directly.
 *
 * See: ai-docs/lexide-layout-plan.md §3.5, §4.4
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useWorkspaceFileStore } from "@/stores/useWorkspaceFileStore";

const LONG_CASE_WITH_FILE =
  /^\/clients\/[^/]+\/matters\/[^/]+\/cases\/[^/]+\/files\/(.+)/;
const PROBE = /^\/__probe\//;

export function WorkspaceFileUrlSync() {
  const pathname = useLocation().pathname;
  const openFile = useWorkspaceFileStore((s) => s.openFile);
  const closeFile = useWorkspaceFileStore((s) => s.closeFile);

  useEffect(() => {
    if (PROBE.test(pathname)) return;

    const match = LONG_CASE_WITH_FILE.exec(pathname);
    if (match) {
      const path = decodeURIComponent(match[1]);
      openFile({ path });
      return;
    }

    // Any non-file path (case-scope, matter, client, /home, ...):
    // reset the pane. closeFile() on an already-null activeFile is
    // a noop, so this is idempotent.
    closeFile();
  }, [pathname, openFile, closeFile]);

  return null;
}
